import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';

import * as fromAuthStore from '@apps/auth/store';
import { IAuroraState } from '@apps/aurora.state';
import { ServerMiscDomainService } from '@apps/bckp/domain/server-misc-domain.service';
import { FilesService, NotfGravity, NotificationService, NotifType, UtilsService } from '@common/services';
import { ActivityByServer } from '@apps/bckp/domain/models/activity/activity-by-server';
import { BckpConstants } from '@apps/bckp/bckp.constants';
import { JobListDomainService } from '@apps/bckp/domain';
import { ResultDomain } from '@common/domain/result-domain';
import { Job } from '@apps/bckp/domain/models';
import { Profile } from '@apps/auth/models';
import { AssetMiscDomainService } from '@apps/cmdb/domain/asset-misc-domain.service';
import { getAllTags } from '@apps/cmdb/store';
import { EnrichedAsset, Tag } from '@apps/cmdb/models';
import { TypeAssetName } from '@apps/cmdb/cmdb.constants';
import { joinAssets } from '@common/operators/join-assets';
import { Assetify } from '@common/domain/assetify';

import * as fromBckpServersHeatActions from './heat.actions';
import { getBckpServersHeatQueryString, getBckpServersHeatIsDataValid, getServersHeatJobsQs, getBckpServersHeatExportQueryString, getAllActivitiesByIdentifier, getAllServersHeat, getServersHeatIssuesOnly } from './heat.selectors';

@Injectable()
export class BckpServersHeatEffects
{
    constructor (
        private actions: Actions,
        private store: Store<IAuroraState>,
        private serverMiscDomainSevice: ServerMiscDomainService,
        private jobListDomainService: JobListDomainService,
        private notificationService: NotificationService,
        private filesService: FilesService,
        private assetMiscDomainService: AssetMiscDomainService
    )
    { }

    bckpServersHeatODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatODataRequested),
                    withLatestFrom(
                        this.store.select(getBckpServersHeatIsDataValid),
                        this.store.select(getAllTags),
                        (_, isDataValid, allTags) => ({
                            isDataValid,
                            allTags
                        })
                    ),
                    filter(({ isDataValid }) => !isDataValid),
                    switchMap(({ allTags }) =>
                        this.store.select(getBckpServersHeatQueryString)
                            .pipe(
                                switchMap((qs: string) =>
                                    this.serverMiscDomainSevice.getActivity(qs)
                                        .pipe(
                                            joinAssets(this.assetMiscDomainService, TypeAssetName.HOST, allTags),
                                            map((servers: ActivityByServer[]) =>
                                                fromBckpServersHeatActions.BckpServersHeatODataLoaded({ servers })
                                            ),
                                            catchError(() =>
                                                of(fromBckpServersHeatActions.BckpServersHeatODataFailed())
                                            )
                                        )
                                )
                            )
                    )
                )
    );

    bckpServersHeatODataFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatODataFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'BACKUP.SERVERS.HEAT.NOTF.LIST.TITLE',
                                'BACKUP.SERVERS.HEAT.NOTF.LIST.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR)
                    )
                ),
        { dispatch: false }
    );

    bckpServersHeatFilterApplied = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatFilterApplied),
                    tap((params: any) =>
                    {
                        this.store.dispatch(fromBckpServersHeatActions.BckpServersHeatCleared());
                        this.store.dispatch(fromBckpServersHeatActions.BckpServersHeatODataInitialized({
                            scrollSize: BckpConstants.QSServersTop,
                            filter: params.filter
                        }));
                        this.store.dispatch(fromBckpServersHeatActions.BckpServersHeatODataRequested());
                    })
                ),
        { dispatch: false }
    );

    bckpServersHeatsJobsRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBckpServersHeatActions.BckpServersHeatJobsRequested),
                withLatestFrom(this.store.select(getServersHeatJobsQs)),
                map(([, qs]: [any, string]) =>
                    qs
                ),
                filter((qs: string) =>
                    !UtilsService.isNullOrEmpty(qs)
                ),
                switchMap((qs: string) =>
                    this.jobListDomainService.getAll(qs)
                        .pipe(
                            map((dataLoaded: ResultDomain<Job>) =>
                                fromBckpServersHeatActions.BckpServersHeatJobsSucceeded({ jobs: dataLoaded.value, nbJobs: dataLoaded.count })
                            ),
                            catchError(() =>
                                of(fromBckpServersHeatActions.BckpServersHeatJobsFailed())
                            ))
                )
            )
    );

    bckpServersHeatJobsFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatJobsFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'BACKUP.SERVERS.HEAT.NOTF.LIST.TITLE',
                                'BACKUP.SERVERS.HEAT.NOTF.LIST.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR)
                    )
                ),
        { dispatch: false }
    );

       bckpServersHeatExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatExportRequested),
                    withLatestFrom(
                        this.store.select(getBckpServersHeatExportQueryString),
                        this.store.select(getAllServersHeat),
                        this.store.select(getServersHeatIssuesOnly())
                    ),
                    switchMap(([params, qs, allServers, displayedServers]:
                        [any, string, ActivityByServer[], ActivityByServer[]]) =>
                        this.serverMiscDomainSevice.exportActivity(
                            this.appendDisplayedServers(qs, allServers, displayedServers),
                            params.format
                        )
                    ),
                    map((response) =>
                        fromBckpServersHeatActions.BckpServersHeatExportSucceeded({ response })
                    )
                )
    );

    bckpServersHeatExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{response: HttpEvent<Blob>}, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );

    bckpServersHeatTagAttachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatTagAttachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.attachTag(TypeAssetName.HOST, params.identifier, params.name, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpServersHeatActions.BckpServersHeatTagAttachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpServersHeatTagAttachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatTagAttachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllActivitiesByIdentifier, { identifier: params.enrichedAsset.identifier }),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([activities, tags]: [ActivityByServer[], Tag[]]) =>
                                {
                                    activities.forEach((activity: ActivityByServer) =>
                                    {
                                        const updatedActivity: Update<ActivityByServer> = {
                                            id: activity.activityId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpServersHeatActions.BckpServersHeatTagAttachSucceeded({ updatedActivity: updatedActivity }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpServersHeatTagAttachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatTagAttachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.ATTACH.TITLE',
                                'CMDB.ASSETS.ATTACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpServersHeatTagDetachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatTagDetachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.detachTag(TypeAssetName.HOST, params.identifier, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpServersHeatActions.BckpServersHeatTagDetachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpServersHeatTagDetachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatTagDetachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllActivitiesByIdentifier, { identifier: params.enrichedAsset.identifier }),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([activities, tags]: [ActivityByServer[], Tag[]]) =>
                                {
                                    activities.forEach((activity: ActivityByServer) =>
                                    {
                                        const updatedServer: Update<ActivityByServer> = {
                                            id: activity.activityId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };
                                        this.store.dispatch(fromBckpServersHeatActions.BckpServersHeatTagDetachSucceeded({ updatedActivity: updatedServer }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpServersHeatTagDetachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersHeatActions.BckpServersHeatTagDetachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.DETACH.TITLE',
                                'CMDB.ASSETS.DETACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );


    private appendDisplayedServers (
        qs: string,
        allServers: ActivityByServer[],
        displayedServers: ActivityByServer[]): string
    {
        if (displayedServers.length === 0 || displayedServers.length === allServers.length)
        {
            return qs;
        }

        const names: string = displayedServers
            .map((server: ActivityByServer) => server.server)
            .filter((name: string) => !!name)
            .join(',');

        return `${qs}&terms=serverShort:${names}`;
    }
}
